import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import './backlog.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useNotify } from '../../components/notifications';
import { GridSearch } from '../../components/GridSearch';
import { navigateToPage } from '../../layouts/navigateTo';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { useAuthStore } from '../../auth/store';
import { navMeta } from '../../layouts/navData';
import { fmtLocalDate, fmtLocalDateTime } from '../../utils/datetime';
import { backlogDashboardId } from '../dashboards/backlogDashboard';
import { DashboardDesignerFrame, type DashboardFrameMode } from '../dashboards/DashboardDesignerFrame';

// ─────────────────────────────────────────────────────────────────────────────
// XML-driven Sample Backlog grid (client request 2026-10-05).
//
// Renders a To Do List → Sample Backlog entry as the OLD native grid, but with
// its COLUMNS and data stored-proc taken from the DevExpress dashboard XML in
// dbo.DashboardData — not hardcoded. The API (/api/v1/backlog/dashboard-grid/{oid})
// parses the dashboard's first Grid item + runs its SP; this page just renders
// the result with the shared grid chrome. So a designer edit to that dashboard's
// grid (add / remove / reorder / rename a column) shows here with NO code change.
//
// Used by BL 08 - Reporting only; the other backlog entries keep the DevExpress
// dashboard viewer (AppShell routing).
// ─────────────────────────────────────────────────────────────────────────────

type Cell = string | number | boolean | null;
type Row = Record<string, Cell>;
interface ColumnRule { interval: string; tone: string; }
interface GridColumn { field: string; caption: string; rules?: ColumnRule[]; }

// Parse a date cell value (ISO "2026-10-05…" OR "MM/DD/YYYY") to {y,m,d}, else null.
function parseDayYMD(v: Cell | undefined): { y: number; m: number; d: number } | null {
  if (v === null || v === undefined) return null;
  const s = String(v);
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);             // ISO
  if (iso) return { y: +iso[1]!, m: +iso[2]!, d: +iso[3]! };
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s);        // MM/DD/YYYY
  if (us) return { y: +us[3]!, m: +us[1]!, d: +us[2]! };
  return null;
}
// True when `day` falls on the given DevExpress Outlook interval (viewer-local).
function dayMatchesInterval(day: { y: number; m: number; d: number }, interval: string): boolean {
  const off = interval === 'today' ? 0 : interval === 'tomorrow' ? 1 : null;
  if (off === null) return false;
  const now = new Date();
  const t = new Date(now.getFullYear(), now.getMonth(), now.getDate() + off);
  return day.y === t.getFullYear() && day.m === t.getMonth() + 1 && day.d === t.getDate();
}
interface GridSection { name: string; columns: GridColumn[]; rows: Row[]; }
// The API still returns `filters` and `kpiPanels`; the UI no longer renders them
// (owner review 2026-10-06), so the type only declares what the page uses.
interface DashboardGrid { title: string; grids: GridSection[]; }

interface Props { navId: string; }

// A dashboard SP may return a column as pre-formatted TEXT (Reporting: "09/29/2026
// 00:00") OR as a real datetime (Missing COC DateReceived: "2026-09-30T14:20:00.000").
// Render an ISO date/datetime through the shared helpers so it shows the house format
// MM/DD/YYYY [HH:mm] (24-hour, no AM/PM — §14b.6 / standing rule 3), never the raw ISO
// (user report 2026-10-05). Any other value — already-formatted date text, ids, names —
// passes through verbatim. Generic: works for a date column in ANY backlog dashboard,
// no per-column config.
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
function formatCellValue(v: Cell | undefined): string {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (ISO_DATETIME.test(s)) return fmtLocalDateTime(s);
  if (ISO_DATE.test(s)) return fmtLocalDate(s);
  return s;
}

// Build the shared AppColumns for ONE grid section. Values render via formatCellValue;
// a column with date-interval rules (DueDay) tints its <td> via cellClass. The JobID
// column double-click runs `jobIdAction` — on the Reporting backlog it opens Custom
// Reporting for that Job (owner review 2026-10-06); elsewhere it copies the Job ID.
// Shared by the grid card and the toolbar export so both stay identical.
function buildGridCols(
  columns: GridColumn[],
  jobIdAction: ((v: string) => void) | undefined,
  jobIdTitle: string,
): AppColumn<Row>[] {
  return columns.map(c => {
    const toText = (r: Row) => formatCellValue(r[c.field]);
    const col: AppColumn<Row> = { field: c.field, header: c.caption, text: toText };
    if (c.rules && c.rules.length > 0) {
      const rules = c.rules;
      col.cellClass = (r: Row) => {
        const day = parseDayYMD(r[c.field]);
        if (!day) return undefined;
        for (const rule of rules) if (dayMatchesInterval(day, rule.interval)) return `bl-cell-${rule.tone}`;
        return undefined;
      };
    }
    if (jobIdAction && c.field.toLowerCase() === 'jobid') {
      col.render = (r: Row) => {
        const s = toText(r);
        return (
          <span
            className="bl-jobid-copy"
            style={{ userSelect: 'text', WebkitUserSelect: 'text', cursor: 'pointer' }}
            title={jobIdTitle}
            onDoubleClick={e => { e.stopPropagation(); if (s) jobIdAction(s); }}
          >{s}</span>
        );
      };
    }
    return col;
  });
}

// ONE grid section (a dashboard main grid) → a white card with the shared AppDataGrid.
// A dashboard may have several (e.g. Subout Tracking: Subout Result Entry + Pending
// Subout Registration), rendered as stacked cards. Selection is PER CARD (its own
// state), so one grid's selection never touches another's.
function GridSectionCard({ section, jobIdAction, jobIdTitle, loading, loadMs }: {
  section: GridSection;
  jobIdAction?: (v: string) => void;
  jobIdTitle: string;
  loading: boolean;
  loadMs?: number | null;
}) {
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [search, setSearch] = useState('');
  const idMap = useRef(new WeakMap<Row, number>());
  const idSeq = useRef(0);
  const rowId = useCallback((row: Row): number => {
    let id = idMap.current.get(row);
    if (id === undefined) { id = idSeq.current++; idMap.current.set(row, id); }
    return id;
  }, []);
  useEffect(() => { setSelected(new Set()); }, [section.rows]);
  useEffect(() => { setSearch(''); }, [section.rows]);

  const gridCols = useMemo(
    () => buildGridCols(section.columns, jobIdAction, jobIdTitle),
    [section.columns, jobIdAction, jobIdTitle]);

  // ONE fuzzy search across EVERY column (owner review 2026-10-06: a single search box
  // like every other BO list, NOT the three G5/XAF filter dropdowns). Matches the
  // displayed text so a date search works on the formatted value.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return section.rows;
    const cols = section.columns;
    return section.rows.filter(row => cols.some(c => formatCellValue(row[c.field]).toLowerCase().includes(q)));
  }, [section.rows, section.columns, search]);

  return (
    <div style={{ marginBottom: 12 }}>
      {/* ONE fuzzy search in its OWN ROW ABOVE the grid — the G6 BO standard (like
          Report Delivery), not inside the grid header (owner review 2026-10-06). */}
      <GridSearch
        value={search}
        onChange={setSearch}
        placeholder="Search…"
        right={filtered.length !== section.rows.length
          ? <span className="search-match-count">{filtered.length} match{filtered.length !== 1 ? 'es' : ''}</span>
          : undefined}
      />
      <div className="bl-card bl-card-flex">
        {/* The grid's own name sits on the LEFT of the grid header, the maximize +
            export icons on the RIGHT (owner review 2026-10-06: "need the title on the
            left-hand side for all backlogs"). For a multi-grid dashboard (Subout) this
            also labels each stacked grid. */}
        <AppDataGrid<Row>
          rows={filtered}
          columns={gridCols}
          getRowId={rowId}
          loading={loading}
          loadMs={loadMs ?? undefined}
          maximizable
          gridTitle={section.name || 'Sample Backlog'}
          selectable
          selectedIds={selected}
          onSelectionChange={setSelected}
          emptyText="No items in this queue"
        />
      </div>
    </div>
  );
}

export default function BacklogXmlGridPage({ navId }: Props) {
  const dashboardId = backlogDashboardId(navId);
  const notify = useNotify();
  const [data,    setData]    = useState<DashboardGrid | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadMs,  setLoadMs]  = useState<number | null>(null);
  const [error,   setError]   = useState<string>();
  const abortRef = useRef<AbortController>();

  // ── "Show Dashboard Designer" / "Show Dashboard in a separate tab" — the legacy
  //    dashboard-view actions, kept on this native grid because they operate on the
  //    SAME dbo.DashboardData dashboard this grid is built from (user report
  //    2026-10-05: these two buttons were missing on BL 08). ──
  const canDesign = useAuthStore(s =>
    (s.permissions?.isAdmin ?? false) || (s.user?.userName ?? '').trim().toLowerCase() === 'service');
  const [designMode, setDesignMode] = useState<DashboardFrameMode | null>(null);

  const load = useCallback(async () => {
    if (!dashboardId) { setError('No dashboard is mapped to this entry.'); return; }
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true); setError(undefined);
    const t0 = performance.now();
    try {
      const res = await api.get<{ success: boolean; data: DashboardGrid }>(
        `/api/v1/backlog/dashboard-grid/${dashboardId}`, { signal: ctrl.signal });
      if (!ctrl.signal.aborted) {
        if (res.data.success) setData(res.data.data);
        else setError('Failed to load data.');
      }
    } catch (e: unknown) {
      if (!ctrl.signal.aborted) {
        const msg = e instanceof Error ? e.message : 'Unknown error';
        if (msg !== 'canceled' && msg !== 'CanceledError') setError(msg);
      }
    } finally {
      if (!ctrl.signal.aborted) { setLoadMs(performance.now() - t0); setLoading(false); }
    }
  }, [dashboardId]);

  useEffect(() => { void load(); }, [load]);

  // Reload when the user RETURNS to this tab after it was hidden — e.g. coming back
  // from the "separate tab" where they edited the dashboard in the designer. This
  // grid is built from the dashboard XML, so a column added / renamed / removed over
  // there must show here without a manual Refresh (user report 2026-10-05: "new tab la
  // column add pannitu save, immediately refresh aagala"). visibilitychange fires on a
  // real tab switch-back (not on every click), so it never thrashes the grid. Skipped
  // while the in-place designer is open — its Back already reloads (closeDesigner).
  const wasHidden = useRef(false);
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'hidden') { wasHidden.current = true; return; }
      if (wasHidden.current) {                       // became visible after being hidden
        wasHidden.current = false;
        if (designMode === null) void load();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [load, designMode]);

  const title = navMeta(navId)?.label ?? 'Sample Backlog';
  // Open the Dashboard Designer frame in VIEW (read-only) mode (user request
  // 2026-10-05): it DISPLAYS the dashboard rather than opening the editable
  // designer toolbox; an admin can still flip to Design with the frame's
  // Design/View toggle.
  const openDesigner = useCallback(() => { if (canDesign) setDesignMode('view'); }, [canDesign]);
  // On return from the designer, reload the grid so any column / SP change made in
  // the dashboard shows here (this grid is XML-driven off that same dashboard).
  const closeDesigner = useCallback(() => { setDesignMode(null); void load(); }, [load]);
  // "Show Dashboard in a separate tab" → the standalone /dashboard-frame page in a NEW
  // tab, so the tab carries the Design / View / Back toolbar (user request 2026-10-05:
  // the separate tab was the bare DevExpress viewer with no buttons).
  // Opened WITHOUT `noopener` on purpose: /dashboard-frame is behind ProtectedRoute,
  // and when "Keep me signed in" is OFF the JWT lives in sessionStorage (per-tab). A
  // `noopener` tab gets a FRESH, empty sessionStorage and so bounces to the login page
  // (user report 2026-10-05). Same-origin, so the browser clones this tab's
  // sessionStorage (the token) into the new tab and the session carries over; it is
  // our own app URL, so the opener reference is not a security concern. Synchronous
  // window.open on the click so it is never treated as a pop-up.
  const openInTab = useCallback(() => {
    if (dashboardId) window.open(`/dashboard-frame/${navId}`, '_blank');
  }, [dashboardId, navId]);

  useHotkeys([
    { combo: 'alt+l', label: 'Show Dashboard Designer', group: title, order: 1,
      disabled: !canDesign || designMode !== null, run: openDesigner },
    { combo: 'alt+t', label: 'Show Dashboard in a separate tab', group: title, order: 2,
      disabled: !dashboardId || designMode !== null, run: openInTab },
  ]);

  // A dashboard has ONE OR MORE grid sections (Subout Tracking has two). Each renders
  // as its own stacked card with its own fuzzy search (owner review 2026-10-06: no KPI
  // cards, no filter dropdowns, no centred dashboard title — follow the G6 BO standard).
  const grids = useMemo(() => data?.grids ?? [], [data]);

  // Copy the Job ID to the clipboard — LEGACY ALPACA PARITY
  // (ShowDetailViewFromDashboardIView.OnViewControlsCreated, "Added logic to copy
  // JobID to clipboard when click data in the Reporting Dashboard"): double-clicking
  // the JobID copies it. Secure-context navigator.clipboard path + a textarea /
  // execCommand fallback for HTTP (localhost is a secure context, so the first path
  // works here); uses the shared notify, not a native alert (standing rule 7).
  const copyJobId = useCallback(async (value: string) => {
    if (!value) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement('textarea');
        ta.value = value; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
      }
      notify.success(`Job ID ${value} copied to clipboard.`);
    } catch {
      notify.error('Could not copy the Job ID to the clipboard.');
    }
  }, [notify]);

  // Reporting backlog: double-clicking a Job ID OPENS the Custom Reporting form loaded
  // with that Job ID (owner review 2026-10-06), via the shared cross-page handoff. Any
  // other backlog keeps the clipboard copy.
  const isReporting = navId === 'bl-report';
  const jobIdAction = useMemo<(v: string) => void>(
    () => isReporting
      ? (v: string) => navigateToPage('custreport', v)
      : (v: string) => { void copyJobId(v); },
    [isReporting, copyJobId]);
  const jobIdTitle = isReporting
    ? 'Double-click to open this Job in Custom Reporting'
    : 'Double-click (or select) to copy the Job ID';

  // Toolbar "Export to" exports the FIRST grid (its columns built the same way the card
  // builds them); each grid card also has its OWN export in its header for the rest.
  const firstGridCols = useMemo<AppColumn<Row>[]>(
    () => (grids[0] ? buildGridCols(grids[0].columns, jobIdAction, jobIdTitle) : []),
    [grids, jobIdAction, jobIdTitle]);

  const count = useMemo(() => grids.reduce((n, g) => n + g.rows.length, 0), [grids]);
  const subtitle = `Read-only · ${loading ? '…' : count} job${count === 1 ? '' : 's'}`;

  // Designer open in place (Show Dashboard Designer) — the shared DevExpress frame;
  // Back returns to this grid and reloads it.
  if (designMode) {
    return (
      <div className="bl-page">
        <DashboardDesignerFrame
          dashboardId={dashboardId!}
          title={title}
          mode={designMode}
          onModeChange={setDesignMode}
          onBack={closeDesigner}
        />
      </div>
    );
  }

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Sample Backlog</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{data?.title || 'Sample Backlog'}</span>
          </div>
          {/* Shared FormTitle — icon/colour/text from the SAME nav entry as the sidebar. */}
          <div className="dp-title-row">
            <FormTitle pageId={navId} loadMs={loadMs} loading={loading} />
          </div>
          <div className="dp-subtitle">{subtitle}</div>
        </div>

        {error && (
          <div className="bl-err">
            <i className="ti ti-alert-circle" style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 5 }} />
            {error}
          </div>
        )}
      </div>

      <div className="bl-grid-area">
        {/* Read-only backlog toolbar (no CRUD): Refresh · Export to. */}
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={
              <>
                {canDesign && (
                  <button type="button" className="ca-btn" onClick={openDesigner}
                          title={`Open this dashboard in the Dashboard Designer (${formatCombo('alt+l')})`}>
                    <i className="ti ti-chart-pie" /> Show Dashboard Designer
                  </button>
                )}
                <button type="button" className="ca-btn" onClick={openInTab} disabled={!dashboardId}
                        title={`Open this dashboard in a new browser tab (${formatCombo('alt+t')})`}>
                  <i className="ti ti-external-link" /> Show Dashboard in a separate tab
                </button>
              </>
            }
            onRefresh={() => void load()}
            refreshing={loading}
            exportData={{ filename: grids[0]?.name || data?.title || 'Sample Backlog', ...gridExportMatrix(grids[0]?.rows ?? [], firstGridCols) }}
          />
        </div>

        {/* Each dashboard grid section → its own white card with ONE fuzzy search in its
            header (owner review 2026-10-06: no KPI cards, no filter dropdowns, no centred
            title — the G6 BO standard). Subout Tracking stacks two cards. loadMs on the
            FIRST card only so the load-time isn't shown twice. */}
        {grids.map((section, i) => (
          <GridSectionCard
            key={section.name || i}
            section={section}
            jobIdAction={jobIdAction}
            jobIdTitle={jobIdTitle}
            loading={loading}
            loadMs={i === 0 ? loadMs : undefined}
          />
        ))}
      </div>
    </div>
  );
}
